import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../i18n';
import { useOrganizations } from '../context/useOrganizations';
import { useApp } from '../context/useApp';
import StyledMarkdown from './StyledMarkdown';
import MarkdownErrorBoundary from './MarkdownErrorBoundary';
import { ArrowLeft, Building2, FileText, Download, ExternalLink, Plus, X, RefreshCw, Sparkles } from 'lucide-react';
import { downloadText } from '../utils/export';
import {
  ORG_DOCUMENTS,
  orgDocumentTitle,
  orgDocumentSummary,
  orgDocumentSectionLabel,
  type OrgDocumentKey,
  type OrgDocumentSection,
} from '../prompts/orgDocuments';

interface OrganizationWorkspaceProps {
  onBack: () => void;
  onOpenOrgCreate: () => void;
  onOpenProject: (projectId: string) => void;
}

type Tab = 'document' | 'linked' | OrgDocumentKey;

export default function OrganizationWorkspace({ onBack, onOpenOrgCreate, onOpenProject }: OrganizationWorkspaceProps) {
  const { t } = useLanguage();
  const o = t.org;
  const {
    activeOrganizationId,
    organizations,
    linkToProject,
    unlinkFromProject,
    generateOrgDocument,
  } = useOrganizations();
  const { projects } = useApp();
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('document');
  // Use a ref for the trigger guard so it persists across renders and is not
  // touched by React's strict-mode double-invoke of effects. A useState guard
  // gets set on the first effect call, then the second strict-mode invocation
  // sees triggered === org.id and bails before the async loop can run — that's
  // the bug that left old orgs stuck with no documents.
  const triggeredRef = useRef<string | null>(null);

  const org = useMemo(
    () => organizations.find((x) => x.id === activeOrganizationId) || null,
    [organizations, activeOrganizationId]
  );

  // Locale used for the org itself may differ from app locale if the user switched.
  const orgLocale = org?.locale === 'de' ? 'de' : 'en';

  // Auto-trigger: when the workspace opens for an org whose document pipeline
  // is not fully complete (every key has a 'done' status and a generated body),
  // kick off the remaining work in the background. Old orgs that pre-date
  // this feature typically have either no status entries or a handful of
  // 'error' entries from a previous run — those should resume, not stay stuck.
  //
  // We use a ref to track which org ids we have already auto-triggered for,
  // and we begin the chain from a fresh `missing` list each time the active
  // org changes. This way React's strict-mode double-invoke of effects, or
  // navigation away/back during generation, cannot starve the pipeline.
  useEffect(() => {
    if (!org || triggeredRef.current === org.id) return;
    const missing = ORG_DOCUMENTS.filter((d) => !org.documents?.[d.key]?.content);
    triggeredRef.current = org.id;
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const meta of missing) {
        if (cancelled) return;
        try {
          await generateOrgDocument(org.id, meta.key);
        } catch (err) {
          // Stop the chain on the first hard failure (e.g. PLAN_LIMIT) so we
          // don't hammer the server after the user has been told to upgrade.
          const code = (err as { code?: string } | undefined)?.code;
          if (code === 'PLAN_LIMIT') return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [org?.id, org?.documents]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!org) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{o.notFoundTitle}</h2>
          <p className="text-slate mb-4">{o.notFoundBody}</p>
          <button
            onClick={onBack}
            className="min-h-[44px] px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium"
          >
            {o.backToList}
          </button>
        </div>
      </div>
    );
  }

  const linkedProjects = (org.linkedProjectIds || [])
    .map((id) => projects.find((p) => p.id === id))
    .filter(Boolean) as typeof projects;
  const linkedProjectIds = new Set(org.linkedProjectIds || []);
  const unlinkedProjects = projects.filter((p) => !linkedProjectIds.has(p.id));

  const handleExport = () => {
    downloadText(`${org.name}.md`, `# ${org.name}\n\n${org.workingDoc || ''}`);
  };

  const handleExportDoc = (key: OrgDocumentKey) => {
    const doc = org.documents?.[key];
    const meta = ORG_DOCUMENTS.find((d) => d.key === key);
    if (!doc || !meta) return;
    downloadText(`${org.name} — ${orgDocumentTitle(meta, orgLocale)}.md`, doc.content);
  };

  const handleRegenerate = (key: OrgDocumentKey) => {
    void generateOrgDocument(org.id, key);
  };

  const statusFor = (key: OrgDocumentKey): string => org.documentsStatus?.[key] || 'pending';
  const isDone = (key: OrgDocumentKey) => Boolean(org.documents?.[key]?.content);

  const doneCount = ORG_DOCUMENTS.filter((d) => isDone(d.key)).length;
  const totalCount = ORG_DOCUMENTS.length;
  const progressPct = Math.round((doneCount / totalCount) * 100);

  // Manual "Generate all" — runs the same pipeline as the auto-trigger, but
  // user-initiated. Useful for old orgs that pre-date the auto-trigger, and
  // for resuming after an interrupted run.
  const [generatingAll, setGeneratingAll] = useState(false);
  const handleGenerateAll = async () => {
    if (generatingAll) return;
    const orgId = org!.id;
    setGeneratingAll(true);
    triggeredRef.current = orgId;
    // Look up the org fresh from context on every step so the latest documents
    // / documentsStatus are visible — that way a doc that just finished (or
    // failed and was retried by another path) is correctly skipped.
    for (const meta of ORG_DOCUMENTS) {
      const fresh = organizations.find((o) => o.id === orgId);
      if (fresh?.documents?.[meta.key]?.content) continue;
      try {
        await generateOrgDocument(orgId, meta.key);
      } catch (err) {
        const code = (err as { code?: string } | undefined)?.code;
        if (code === 'PLAN_LIMIT') break;
      }
    }
    setGeneratingAll(false);
  };

  // Group documents by section for the rail.
  const grouped: Array<{ section: OrgDocumentSection; items: typeof ORG_DOCUMENTS }> = [];
  for (const meta of ORG_DOCUMENTS) {
    const last = grouped[grouped.length - 1];
    if (last && last.section === meta.section) {
      last.items.push(meta);
    } else {
      grouped.push({ section: meta.section, items: [meta] });
    }
  }

  return (
    <div className="flex h-full flex-col">
      <header className="bg-white border-b border-cloud px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-2 sm:gap-4 flex-wrap sm:flex-nowrap">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={onBack}
              className="p-2 -ml-1 text-ash hover:text-ink hover:bg-mist transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Back"
              title="Back"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="w-9 h-9 bg-red-50 flex items-center justify-center shrink-0">
              <Building2 size={18} className="text-red-500" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-ink truncate">{org.name}</h1>
              <p className="text-xs sm:text-sm text-slate truncate">
                {org.industry || '—'} · {org.location || '—'}
                <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-mist text-slate border border-cloud">
                  {org.locale === 'de' ? 'DE' : 'EN'}
                </span>
                <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-red-50 text-red-600 border border-red-100">
                  {doneCount} / {totalCount} {o.docsLabel}
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {doneCount < totalCount && (
              <button
                onClick={handleGenerateAll}
                disabled={generatingAll}
                className="flex-1 sm:flex-none min-h-[44px] px-3 py-2 bg-red-500 hover:bg-red-600 text-white font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                <Sparkles size={16} className={generatingAll ? 'animate-pulse' : ''} />
                {generatingAll ? o.generatingDoc : o.generateAll}
              </button>
            )}
            <button
              onClick={handleExport}
              className="flex-1 sm:flex-none min-h-[44px] px-3 py-2 text-ink hover:bg-mist font-medium flex items-center justify-center gap-2 border border-cloud transition-colors"
            >
              <Download size={16} /> {o.export}
            </button>
            <button
              onClick={onOpenOrgCreate}
              className="flex-1 sm:flex-none min-h-[44px] px-3 py-2 text-red-600 hover:bg-red-50 font-medium flex items-center justify-center gap-2 border border-red-100 transition-colors"
            >
              <FileText size={16} /> {o.createFromOrgCta}
            </button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3 h-1.5 w-full bg-mist overflow-hidden" aria-label={`${doneCount} of ${totalCount} documents generated`}>
          <div
            className="h-full bg-red-500 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop section rail */}
        <nav className="hidden md:block w-64 bg-white border-r border-cloud overflow-y-auto shrink-0">
          <RailItem
            active={tab === 'document'}
            onClick={() => setTab('document')}
            label={o.documentTitle}
            status={org.workingDoc ? 'done' : 'pending'}
          />
          <RailItem
            active={tab === 'linked'}
            onClick={() => setTab('linked')}
            label={o.linkedProjects}
            status={linkedProjects.length > 0 ? 'done' : 'pending'}
            count={linkedProjects.length}
          />
          <div className="border-t border-cloud mt-2 pt-2">
            <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-slate">
              {o.docsRailHeader}
            </p>
            {grouped.map((group) => (
              <div key={group.section} className="mb-1">
                <p className="px-4 py-1 text-[11px] font-semibold uppercase tracking-wider text-ash">
                  {orgDocumentSectionLabel(group.section, orgLocale)}
                </p>
                {group.items.map((meta) => {
                  const status = statusFor(meta.key);
                  const active = tab === meta.key;
                  return (
                    <button
                      key={meta.key}
                      onClick={() => setTab(meta.key)}
                      className={`w-full text-left px-4 py-2 flex items-center justify-between gap-2 transition-colors border-l-4 ${
                        active
                          ? 'bg-red-50 border-l-red-500'
                          : 'hover:bg-mist border-l-transparent'
                      }`}
                    >
                      <span className={`text-sm truncate ${active ? 'text-ink font-medium' : 'text-slate'}`}>
                        {orgDocumentTitle(meta, orgLocale)}
                      </span>
                      <StatusDot status={status} />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </nav>

        {/* Mobile horizontal tablist */}
        <nav className="md:hidden bg-white border-b border-cloud overflow-x-auto flex shrink-0">
          <MobileTab active={tab === 'document'} onClick={() => setTab('document')} label={o.documentTitle} />
          <MobileTab active={tab === 'linked'} onClick={() => setTab('linked')} label={o.linkedProjects} />
          {ORG_DOCUMENTS.map((meta) => (
            <MobileTab
              key={meta.key}
              active={tab === meta.key}
              onClick={() => setTab(meta.key)}
              label={orgDocumentTitle(meta, orgLocale)}
              status={statusFor(meta.key)}
            />
          ))}
        </nav>

        <section className="flex-1 overflow-y-auto bg-paper p-4 sm:p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {tab === 'document' && (
              <>
                <div className="bg-white border border-cloud p-5 sm:p-6">
                  <h2 className="text-base font-semibold text-slate uppercase tracking-wider mb-3">
                    {o.documentTitle}
                  </h2>
                  {org.workingDoc && org.workingDoc.trim() ? (
                    <MarkdownErrorBoundary content={org.workingDoc}>
                      <StyledMarkdown content={org.workingDoc} />
                    </MarkdownErrorBoundary>
                  ) : (
                    <p className="text-sm text-ash italic">{o.documentEmpty}</p>
                  )}
                </div>

                {org.extract && Object.keys(org.extract).length > 0 && (
                  <div className="bg-white border border-cloud p-5 sm:p-6">
                    <h2 className="text-base font-semibold text-slate uppercase tracking-wider mb-3">
                      {o.extractTitle}
                    </h2>
                    <pre className="text-xs whitespace-pre-wrap text-slate bg-paper p-3 border border-cloud overflow-x-auto">
                      {JSON.stringify(org.extract, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}

            {tab === 'linked' && (
              <div className="bg-white border border-cloud p-5 sm:p-6">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <h2 className="text-base font-semibold text-slate uppercase tracking-wider">
                    {o.linkedProjects}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setProjectPickerOpen((v) => !v)}
                    className="min-h-[36px] px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-1.5 border border-red-100 transition-colors"
                  >
                    {projectPickerOpen ? <X size={14} /> : <Plus size={14} />}
                    {o.addProjectCta}
                  </button>
                </div>

                {projectPickerOpen && (
                  <div className="mb-4 border border-mist p-3 bg-paper">
                    {unlinkedProjects.length === 0 ? (
                      <p className="text-sm text-ash italic">{o.addProjectEmpty}</p>
                    ) : (
                      <ul className="divide-y divide-mist">
                        {unlinkedProjects.map((p) => (
                          <li key={p.id} className="py-2 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-medium text-ink truncate">{p.name}</div>
                              <div className="text-xs text-slate truncate">{p.input?.idea || '—'}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                void linkToProject(org.id, p.id);
                              }}
                              className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-red-100 flex items-center gap-1.5 transition-colors shrink-0"
                            >
                              <Plus size={14} /> {o.addProjectCta}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {linkedProjects.length === 0 ? (
                  <p className="text-sm text-ash italic">{o.noLinkedProjects}</p>
                ) : (
                  <ul className="divide-y divide-mist">
                    {linkedProjects.map((p) => (
                      <li
                        key={p!.id}
                        className="py-3 flex items-center justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-ink truncate">{p!.name}</div>
                          <div className="text-xs text-slate truncate">{p!.input?.idea || '—'}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => onOpenProject(p!.id)}
                            className="min-h-[44px] px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 border border-red-100 flex items-center gap-1.5 transition-colors"
                          >
                            <ExternalLink size={14} /> {o.openProject}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm(o.unlinkProjectConfirm(p!.name))) {
                                void unlinkFromProject(org.id, p!.id);
                              }
                            }}
                            className="min-h-[44px] px-3 py-2 text-sm font-medium text-ash hover:bg-mist border border-cloud flex items-center gap-1.5 transition-colors"
                            aria-label={`${o.unlinkProject} ${p!.name}`}
                          >
                            <X size={14} /> {o.unlinkProject}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab !== 'document' && tab !== 'linked' && (
              <DocumentPanel
                meta={ORG_DOCUMENTS.find((d) => d.key === tab)!}
                content={org.documents?.[tab]?.content}
                status={statusFor(tab)}
                errorMessage={org.documentsErrors?.[tab]}
                locale={orgLocale}
                onRegenerate={() => handleRegenerate(tab)}
                onExport={() => handleExportDoc(tab)}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const symbol =
    status === 'done' ? '✓' :
    status === 'generating' ? '⏳' :
    status === 'error' ? '!' :
    '○';
  const color =
    status === 'done' ? 'text-green-600' :
    status === 'generating' ? 'text-amber-500 animate-pulse' :
    status === 'error' ? 'text-red-500' :
    'text-ash';
  return <span className={`text-xs shrink-0 ${color}`} aria-label={status}>{symbol}</span>;
}

function RailItem({
  active,
  onClick,
  label,
  status,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  status?: string;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-mist flex items-center justify-between transition-colors border-l-4 ${
        active
          ? 'bg-red-50 border-l-red-500'
          : 'hover:bg-mist border-l-transparent'
      }`}
    >
      <span className={`font-medium truncate ${active ? 'text-ink' : 'text-slate'}`}>{label}</span>
      <span className="flex items-center gap-2 shrink-0">
        {typeof count === 'number' && (
          <span className="text-xs text-ash">{count}</span>
        )}
        {status && <StatusDot status={status} />}
      </span>
    </button>
  );
}

function MobileTab({
  active,
  onClick,
  label,
  status,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  status?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-1.5 min-h-[44px] ${
        active ? 'text-red-600 border-red-500' : 'text-slate border-transparent hover:text-ink'
      }`}
    >
      {label}
      {status && <StatusDot status={status} />}
    </button>
  );
}

function DocumentPanel({
  meta,
  content,
  status,
  errorMessage,
  locale,
  onRegenerate,
  onExport,
}: {
  meta: (typeof ORG_DOCUMENTS)[number];
  content: string | undefined;
  status: string;
  errorMessage?: string;
  locale: 'en' | 'de';
  onRegenerate: () => void;
  onExport: () => void;
}) {
  const { t } = useLanguage();
  const o = t.org;

  const isGenerating = status === 'generating';
  const isError = status === 'error';
  const title = orgDocumentTitle(meta, locale);
  const summary = orgDocumentSummary(meta, locale);

  return (
    <div className="bg-white border border-cloud p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-ash mb-1">
            {orgDocumentSectionLabel(meta.section, locale)}
          </p>
          <h2 className="text-xl font-bold text-ink">{title}</h2>
          <p className="text-sm text-slate mt-1">{summary}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onRegenerate}
            disabled={isGenerating}
            className="min-h-[36px] px-3 py-1.5 text-sm font-medium text-ink hover:bg-mist border border-cloud flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            {isGenerating ? <Sparkles size={14} className="animate-pulse" /> : <RefreshCw size={14} />}
            {o.regenerate}
          </button>
          <button
            type="button"
            onClick={onExport}
            disabled={!content}
            className="min-h-[36px] px-3 py-1.5 text-sm font-medium text-ink hover:bg-mist border border-cloud flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <Download size={14} /> {o.exportDoc}
          </button>
        </div>
      </div>

      {isGenerating && (
        <div className="bg-amber-50 border border-amber-100 text-amber-800 text-sm p-3 flex items-center gap-2 mb-4">
          <Sparkles size={16} className="shrink-0 animate-pulse" />
          {o.generatingDoc}
        </div>
      )}

      {isError && !isGenerating && (
        <div className="bg-red-50 border border-red-100 text-red-700 text-sm p-3 mb-4">
          <p className="font-medium mb-1">{o.errorDoc}</p>
          {errorMessage && (
            <details className="mt-1">
              <summary className="cursor-pointer text-xs text-red-600 underline">
                Technical details
              </summary>
              <pre className="mt-1 text-xs whitespace-pre-wrap break-words text-red-800 bg-white border border-red-100 p-2 max-h-40 overflow-y-auto">
                {errorMessage}
              </pre>
            </details>
          )}
        </div>
      )}

      {content ? (
        <MarkdownErrorBoundary content={content}>
          <StyledMarkdown content={content} />
        </MarkdownErrorBoundary>
      ) : (
        !isGenerating && (
          <p className="text-sm text-ash italic">
            {status === 'pending' ? o.docPending : o.docEmpty}
          </p>
        )
      )}
    </div>
  );
}